import {Fragment, useEffect, useState, useCallback} from 'react';
import Container from '../common/layout/Container';
import Error from '../common/Error';
import Spinner from '../common/Spinner';
import {
  getAllTreatmentCategories,
  deleteCategory,
  deleteGroup,
  deleteTreatment,
  updateCategory,
  updateGroup, updateTreatment, createCategory, createGroup, createTreatment
} from '../../api/treatments';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {
  CategoryProps,
  TreatmentProps,
  DeletionInformationProps,
  CreationInformationProps
} from './types';
import permissionsInfo from '../../constants/permissionsInfo';
import {useAppDispatch} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Row from './Row';
import {
  getFormattedName,
  getTreatmentFormattedName,
  getCurrentEntityObject
} from './helpers';
import {Button} from '../common/Button';
import CreationPopup from '../common/popup/CreationPopup';
import {
  treatmentValidationSchema,
  treatmentWithCodeValidationSchema
} from '../../constants/validationSchemes';
import EnsurePermissions from '../common/EnsurePermission';
import RequestError from '../../api/RequestError';
import {handleApiValidationErrors} from '../../helpers/error';
import {sanitize} from '../../helpers';
import TableHeaderRow from '../common/TableHeaderRow';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const entity = {
  header: 'Treatment Configuration',
  title: 'List of Treatments',
  loadingText: 'Getting treatments list',
  fields: [{
    name: 'treatment',
    label: 'Treatment',
    editable: true,
    permission: permissionsInfo.operatories.edit
  },
  {
    name: 'delete',
    label: '',
    permission: permissionsInfo.operatories.edit
  },
  {
    name: 'add',
    label: '',
    permission: permissionsInfo.operatories.edit
  }
  ]
};

const requests = {
  category: {
    create: createCategory,
    delete: deleteCategory,
    update: updateCategory,
    creationInputs: [{name: 'name', label: 'Category Name', value: ''}],
    validationSchema: treatmentValidationSchema,
    popupLabel: (name: string) =>
      `The Category "${name}" will be removed`
  },
  group: {
    create: createGroup,
    delete: deleteGroup,
    update: updateGroup,
    creationInputs: [{name: 'name', label: 'Group Name', value: ''}],
    validationSchema: treatmentValidationSchema,
    popupLabel: (name: string) =>
      `The Group "${name}" will be removed`
  },
  treatment: {
    create: createTreatment,
    delete: deleteTreatment,
    update: updateTreatment,
    creationInputs: [
      {name: 'code', label: 'Treatment Code', value: ''},
      {name: 'name', label: 'Treatment Name', value: ''}
    ],
    validationSchema: treatmentWithCodeValidationSchema,
    popupLabel: (name: string) =>
      `The Treatment "${name}" will be removed`
  }
};

export const Treatments = () => {
  const {t} = useTranslation('treatments');
  const dispatch = useAppDispatch();
  const [isPopupShown, setIsPopupShown] = useState(false);
  const [isCreationPopupShown, setIsCreationPopupShown] = useState(false);

  const [treatmentsData, setTreatmentsData] =
  useState<CategoryProps[]>();
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDeleting, setDeleting] = useState(false);
  const [deletionInformation, setDeletionInformation] =
  useState<DeletionInformationProps | null>(null);
  const [creationInformation, setCreationInformation] =
    useState<CreationInformationProps | null>(null);
  const [apiValidationError, setApiValidationError] =
    useState<Nullable<Dictionary<string[]>>>(null);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const getAllTreatments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllTreatmentCategories();
      setTreatmentsData(response);
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllTreatments();

    return () => {
      setError('');
    };
  }, [getAllTreatments]);

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const handleDelete = async () => {
    if (!deletionInformation) return;
    const {id, categoryName} = deletionInformation;

    try {
      setDeleting(true);
      await requests[categoryName].delete(id);
      addSuccessToast(t('toasts.removeGroupMessage', {categoryName}));
      const response = await getAllTreatmentCategories();
      setTreatmentsData(response);
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsPopupShown(false);
      setDeleting(false);
    }
  };

  const handleUpdate = async (
      id: number,
      values: any,
      categoryName: string
  ) => {
    const currentEntity =
      getCurrentEntityObject(id, categoryName, treatmentsData || []);
    const requestBody = sanitize({...currentEntity, ...values});

    try {
      await requests[categoryName].update(id, requestBody);
      addSuccessToast(t('toasts.updateGroupMessage', {categoryName}));
      const response = await getAllTreatmentCategories();
      setTreatmentsData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const handleCreate = async (values: any) => {
    if (!creationInformation) return;
    const {id, categoryName} = creationInformation;
    try {
      if (id) {
        await requests[categoryName].create(id, values);
      } else {
        await requests[categoryName].create(values);
      }

      addSuccessToast(t('toasts.createGroupMessage', {categoryName}));
      const response = await getAllTreatmentCategories();
      setTreatmentsData(response);
      setIsCreationPopupShown(false);
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, categoryName);
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };


  const getEditableCellInfo = (
      rowId: number,
      cellData: {name: string, code?: string},
      categoryName: string,
      permission: string[] | null
  ) => ({
    rowId: rowId,
    cellData: cellData,
    editHandler: (id, values) => handleUpdate(id, values, categoryName),
    permission
  });

  const renderRows = () => {
    if (!treatmentsData) return null;

    return treatmentsData.map((category: CategoryProps) => {
      const {name, id, immutable, groupChains} = category || {};

      const getCategoryContent = () => {
        const cellItem = getEditableCellInfo(
            id,
            {'name': name},
            'category',
            permissionsInfo.treatments.edit
        );

        return <Row
          className="category"
          key={`category-${id}`}
          isImmutable={immutable}
          cellItem={cellItem}
          addButtonLabel="Add Group"
          handleDelete={() => {
            setIsPopupShown(true);
            setDeletionInformation({id, categoryName: 'category', name});
          }}
          handleCreate={() => {
            setCreationInformation({id, categoryName: 'group'});
            setIsCreationPopupShown(true);
          }}
        />;
      };

      const getTreatmentsContent = (treatments: TreatmentProps[][]) => {
        return treatments.map((treatmentList) => {
          const isImmutable = treatmentList[0]?.immutable;

          const id = treatmentList[0]?.id;
          const code = treatmentList[0]?.code;
          const name = isImmutable ?
            getTreatmentFormattedName(treatmentList) :
            treatmentList[0]?.name;

          const cellItem = getEditableCellInfo(
              id,
              {'code': code, 'name': name},
              'treatment',
              permissionsInfo.treatments.edit
          );

          return <Row
            key={`treatment-${id}`}
            className="treatment"
            isImmutable={isImmutable}
            cellItem={cellItem}
            handleDelete={() => {
              setIsPopupShown(true);
              setDeletionInformation({
                id,
                categoryName: 'treatment',
                name});
            }}
            handleCreate={() => {
              setCreationInformation({id, categoryName: 'none'});
              setIsCreationPopupShown(true);
            }}
          />;
        });
      };

      const getGroupContent = () => groupChains.map((groupChain) => {
        const {groups, treatments} = groupChain || {};

        const name = getFormattedName(groups);
        const id = groups[0]?.id;

        const isImmutable = groups[0]?.immutable;
        const cellItem = getEditableCellInfo(
            id,
            {'name': name},
            'group',
            permissionsInfo.treatments.edit
        );

        return (
          <Fragment key={`group-${id}`}>
            <Row
              className="group"
              isImmutable={isImmutable}
              cellItem={cellItem}
              addButtonLabel="Add Treatment"
              handleDelete={() => {
                setIsPopupShown(true);
                setDeletionInformation({
                  id, categoryName: 'group', name
                });
              }}
              handleCreate={() => {
                setCreationInformation({id, categoryName: 'treatment'});
                setIsCreationPopupShown(true);
              }}
            />
            {getTreatmentsContent(treatments)}
          </Fragment>);
      });

      return (
        <Fragment key={id}>
          {getCategoryContent()}
          {getGroupContent()}
        </Fragment>
      );
    });
  };

  const getPopupLabel = () => {
    if (! deletionInformation) return null;

    const {categoryName, name}: DeletionInformationProps =
        deletionInformation;

    return requests[categoryName]?.popupLabel(name);
  };

  return (
    <>
      <Container header={entity?.header}>
        <>
          <div className="list-header">
            <h2 className="subheader">{entity?.title}</h2>
          </div>

          {isLoading ?
            <Spinner loadingText={entity?.loadingText} /> :
            <>
              <table className="table treatments">
                <thead>
                  <TableHeaderRow
                    tableData={entity.fields}
                    headerRowClassName="header-row position-relative"
                  />
                </thead>
                <tbody>
                  {renderRows()}
                  <EnsurePermissions
                    elementPermissions={permissionsInfo.treatments.create} >
                    <tr>
                      <td className="add-category-button">
                        <Button
                          label="Add Category"
                          onClick={() => {
                            setCreationInformation({
                              id: null,
                              categoryName: 'category'
                            });
                            setIsCreationPopupShown(true);
                          }} />
                      </td>
                      <td/>
                      <td/>
                      <td/>
                      <td/>
                    </tr>
                  </EnsurePermissions>
                </tbody>
              </table>
            </>}

          {error && <Error message={error} />}
        </>
      </Container>

      <ConfirmationPopup
        isActionProcessing={isDeleting}
        labels={{body: getPopupLabel()}}
        isOpen={isPopupShown}
        setIsOpen={setIsPopupShown}
        confirmableAction={handleDelete}
      />

      {creationInformation ?
        <CreationPopup
          inputFields=
            {requests?.[creationInformation?.categoryName]?.creationInputs}
          validationSchema=
            {requests?.[creationInformation?.categoryName]?.validationSchema}
          isOpen={isCreationPopupShown}
          setIsOpen={setIsCreationPopupShown}
          confirmableAction={handleCreate}
          apiValidationError={apiValidationError}
          setApiValidationError={setApiValidationError}
        /> : null}
    </>
  );
};

export default Treatments;
